<template>
    <v-card flat class="mt-n2" color="transparent">
      <v-autocomplete
        :prepend-inner-icon="icon"
        v-model="selectedId"
        :items="items"
        :loading="loading"
        :label="label"
        item-text="name" 
        item-value="id"
        outlined
        dense
        clearable
        @change="getSelection"
      >
        <template v-slot:prepend-item>
          <v-list-item ripple @click="openDialog('add')" v-if="isAdmin">
            <!-- <v-list-item-icon><v-icon color="success">mdi-plus-circle</v-icon></v-list-item-icon> -->
            <v-list-item-content>
              <v-list-item-title class="success--text font-weight-bold"><v-btn color="primary" block><v-icon dark left>mdi-plus-circle</v-icon> Add New {{ formatName(table) }}</v-btn></v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider class="mb-2"></v-divider>
        </template>
  
        <template v-slot:item="{ item }">
          <v-list-item-content>
            <v-list-item-title class="text-wrap">
              {{ item.name }} 
              <span v-if="Object.keys(item).length > 2" class="grey--text d-block">
                {{ getSubtext(item) }}
              </span>
            </v-list-item-title>
          </v-list-item-content>
  
          <v-list-item-action class="flex-row" v-if="isAdmin">
            <v-btn icon small color="primary" @click.stop="openDialog('edit', item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small color="error" @click.stop="openDialog('delete', item)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </v-list-item-action>
        </template>
      </v-autocomplete>
  
      <v-dialog v-model="dialog" max-width="500px" persistent>
        <v-card>
          <v-card-title class="headline grey lighten-2 uppercase">
            {{ mode }} {{ table }}
          </v-card-title>
  
          <v-card-text class="mt-4">
            <v-container v-if="mode !== 'delete'">
              <v-row>
                <v-col v-for="col in columns" :key="col" cols="12">
                    <AutoCompleteCrudAdvanced
                    v-if="relations[col]"
                    v-model="editedItem[col]"
                    :table="relations[col].relationTable"
                    :label="formatLabel(col)"
                    @input="(val) => { editedItem[col] = val ? val.id : null }"
                    ></AutoCompleteCrudAdvanced>
                  
                  <v-menu
                    v-else-if="isDateColumn(col)"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="editedItem[col]"
                        :label="formatLabel(col)"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="editedItem[col]" no-title scrollable></v-date-picker>
                  </v-menu>
  
                  <template v-else-if="isColorColumn(col)">
                    <p class="mb-1 grey--text text--darken-1">{{ formatLabel(col) }}</p>
                    <v-color-picker
                      v-model="editedItem[col]"
                      flat
                      dot-size="25"
                      swatches-max-height="200"
                      class="mb-4"
                    ></v-color-picker>
                  </template>
  
                  <v-text-field
                    v-else
                    v-model="editedItem[col]"
                    :label="formatLabel(col)"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
            
            <v-alert v-else type="warning" text>
              Are you sure you want to delete this record?
            </v-alert>
          </v-card-text>
  
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="dialog = false">Cancel</v-btn>
            <v-btn :color="mode === 'delete' ? 'error' : 'success'" depressed :loading="saving" @click="save">
              {{ mode === 'delete' ? 'Confirm Delete' : 'Save Changes' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </template>
  
  <script>
  import axios from "@/plugins/axios"
  import helpersJs from "@/plugins/helpers"
  
  export default {
    name:"AutoCompleteCrudAdvanced",
    props: {
      triggerRefresh:{type: Number, default: 0},
      value: [String, Number, Object],
      table: { type: String, required: true },
      label: { type: String, default: "Select Item" },
      icon:{type:String, default:"mdi-magnify"},
      formatter:{type:Function, default:null} //must return eg. {name:'formatted display name', id: 2}
      // Sample formatter for invoices formatInvoices(invoices){
      //       // if(!invoices || !invoices.length) return []
      //       const formattedInvoices = [];
      //       invoices.forEach(invoice => {
      //           formattedInvoices.push({
      //               ...invoice,
      //               name: invoice.description,
      //               id: invoice.id
      //           })
      //       });
      //       return formattedInvoices;
      //   },
    },
    data: () => ({
      items: [],
      columns: [],
      relations: {}, // To store { branch_id: [{id:1, name:'Main'}, ...] }
      //hiddenFields: ['id', 'emis', 'created_at', 'updated_at', 'password'],
      loading: false,
      saving: false,
      dialog: false,
      mode: 'add',
      selectedId: null,
      editedItem: {},
    }),
    computed: {
      getAuthenticated() { return this.$store.getters.getAuthenticated },
      getAccessToken() { return this.$store.getters.getAccessToken },
      isAdmin() { return true } //this.$store.getters.isAdmin; },
    },
    watch: {
      triggerRefresh:{
        handler() { this.initializeComponent() },
        immediate: true
      },
      value: {
        handler(val) { 
          this.selectedId = (val && typeof val === 'object') ? val.id : val;
        },
        immediate: true
      },
      table: {
        handler() { this.initializeComponent() },
        immediate: true
      }
    },
    
    methods: {
      async initializeComponent() {
        await this.fetchItems();
        //this.deriveColumns();
      },

      formatName(name){
        return helpersJs.formatUnderscoreNamesWithFirstCapitalLetters(name)
      },

  async fetchItems() {
    if(!this.getAuthenticated || !this.getAccessToken) return;

    this.loading = true;
    try {
      const res = await axios.postDataWithToken("/auth/get-table-items-with-table-name", {
        table: this.table
      }, this.getAccessToken);

      if (res && res.status === "success") {
        if(this.formatter){
          this.items = this.formatter(res.items)
        }else{
          this.items = res.items;
        }
        
        this.columns = res.columns;
        this.relations = res.relations || {}; // Store the dropdown options
      }
    } catch (e) {
      console.error("Fetch failed", e);
    } finally {
      this.loading = false;
    }
  },

    //not used anymore
      deriveColumns() {
        if (this.items.length > 0) {
          // Get all keys from the first item and filter out hidden ones
          this.columns = Object.keys(this.items[0]).filter(key => !this.hiddenFields.includes(key));
        }
      },
  
      isDateColumn(col) {
        const datesColumns = ['birthday','dob','expiry','start','end']
        const column = col.toLowerCase();
        return column.includes('date') || datesColumns.includes(column);
      },
  
      isColorColumn(col) {
        return col.toLowerCase().includes('color');
      },
  
      formatLabel(col) {
        // Converts "client_name" to "Client Name"
        if(col === "dob"){return "Date of Birth"}
        return col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      },

  getSubtext(item) {
    let secondaryCol = '';
    if(item && (item['make'] || item['model'])){
      const subText = `${item['make'] || ''}` + ` ${item['model'] || ''} - ${item['year'] || ''} | ${item['license_plate'] || ''}`
      return subText;
    }else if(item && item['description']){
      secondaryCol = 'description'
    }else if(item && item['institution']){
      secondaryCol = 'institution'
    }else{
      secondaryCol = this.columns.find(c => c !== 'name' && c !== 'id');
    }
    
    if (!secondaryCol || !this.relations[secondaryCol]) return item[secondaryCol];

    // Access .items because of the new structure
    const relatedList = this.relations[secondaryCol].items;
    const relatedItem = relatedList.find(r => r.id === item[secondaryCol]);
    
    return relatedItem ? relatedItem.name : item[secondaryCol];
  },

  // Added a helper to handle the dynamic label for the select
  getRelationLabel(col) {
      if (col === 'branch_id') return 'Select Local Branch';
      return this.formatLabel(col);
  },
  
      openDialog(mode, item = null) {
        this.mode = mode;
        if (item) {
          this.editedItem = { ...item };
        } else {
          
          // Create empty object based on discovered columns
          let newItem = {};
          this.columns.forEach(col => newItem[col] = '');
          this.editedItem = newItem;
        }
        this.dialog = true;
      },
  
      async save() {
        this.saving = true;
        try {
          //copy data to be save in order not to update ui during changes here
          const dataToSave = { ...this.editedItem };

          //look for color columns and turn them into hex code for easy storage
          Object.keys(dataToSave).forEach(key => {
            if (this.isColorColumn(key) && typeof dataToSave[key] === 'object') {
              dataToSave[key] = dataToSave[key].hex; // Extract just the hex
            }
          });

          const res = await axios.postDataWithToken(`/auth/add-edit-delete-autocomplete`, {
            ...dataToSave,
            table: this.table,
            mode: this.mode
          }, this.getAccessToken);
  
          if (res && res.status === 'success') {
        
            await this.fetchItems();
            this.dialog = false;
            if (this.mode === 'delete' && this.selectedId === this.editedItem.id) {
              this.selectedId = null;
              this.$emit('input', null);
            }
          
            if(this.mode == 'add'){  
              this.selectedId = res?.data?.id || null;
              if(this.selectedId){
                this.getSelection()
              }
              
            }
          }
        } finally {
          this.saving = false;
        }
      },
  
      getSelection() {
        const selectedItem = this.items.find(item => item.id === this.selectedId);
        this.$emit('input', selectedItem);
      }
    }
  }
  </script>